/**
 * useMistakeFilters Hook
 * Manages the current filter state for mistake book
 */

import { useState, useCallback, useMemo } from 'react';
import {
  type MistakeFilters,
  type SortField,
  type SortDirection,
  DEFAULT_FILTERS,
} from '../services/mistakeFilterModel';

export interface UseMistakeFiltersReturn {
  filters: MistakeFilters;
  setExamType: (value: string | null) => void;
  setSection: (value: string | null) => void;
  setDifficulty: (value: string | null) => void;
  setSortBy: (field: SortField) => void;
  setSortDirection: (direction: SortDirection) => void;
  toggleSortDirection: () => void;
  resetFilters: () => void;
  hasActiveFilters: boolean;
}

export function useMistakeFilters(): UseMistakeFiltersReturn {
  const [filters, setFilters] = useState<MistakeFilters>(DEFAULT_FILTERS);

  const setExamType = useCallback((value: string | null) => {
    setFilters((prev) => ({ ...prev, examType: value }));
  }, []);

  const setSection = useCallback((value: string | null) => {
    setFilters((prev) => ({ ...prev, section: value }));
  }, []);

  const setDifficulty = useCallback((value: string | null) => {
    setFilters((prev) => ({ ...prev, difficulty: value }));
  }, []);

  const setSortBy = useCallback((field: SortField) => {
    setFilters((prev) => ({ ...prev, sortBy: field }));
  }, []);

  const setSortDirection = useCallback((direction: SortDirection) => {
    setFilters((prev) => ({ ...prev, sortDirection: direction }));
  }, []);

  const toggleSortDirection = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      sortDirection: prev.sortDirection === 'asc' ? 'desc' : 'asc',
    }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS);
  }, []);

  const hasActiveFilters = useMemo(
    () => filters.examType !== null || filters.section !== null || filters.difficulty !== null,
    [filters]
  );

  return {
    filters,
    setExamType,
    setSection,
    setDifficulty,
    setSortBy,
    setSortDirection,
    toggleSortDirection,
    resetFilters,
    hasActiveFilters,
  };
}
